/**
 * Web-side mirror of `shared/src/util/housing.ts` —
 * `aggregateDeductibility` rollup used by the per-unit drilldown's
 * Expenses tab. Web does NOT import from `@hearth/shared` (locked since
 * 5.1; pulls `node:crypto` into the Vite bundle). Behavior must stay in
 * sync with the shared implementation; the matching
 * shared/test/util-housing.test.ts cases are the contract.
 */

/**
 * Minimal subset of HousingExpense the rollup needs. Cleave 2 adds the
 * canonical `HousingExpense` type to `web/src/lib/api-modules/types.ts`;
 * the rollup is structurally typed so anything carrying these fields
 * works.
 */
export interface DeductibilityExpenseRow {
  readonly amount_cents: number;
  readonly payment_date: string; // ISO YYYY-MM-DD
  readonly deductible_mortgage_interest_cents?: number;
  readonly deductible_property_tax_cents?: number;
  readonly non_deductible_portion_cents?: number;
}

export interface DeductibilityRollup {
  readonly mortgage_interest_total_cents: number;
  readonly property_tax_total_cents: number;
  readonly non_deductible_total_cents: number;
  readonly total_paid_cents: number;
  readonly expense_count: number;
}

export function aggregateDeductibility(
  expenses: readonly DeductibilityExpenseRow[],
  year: number,
): DeductibilityRollup {
  const yearPrefix = `${year}-`;
  let mortgageInterest = 0;
  let propertyTax = 0;
  let nonDeductible = 0;
  let totalPaid = 0;
  let count = 0;
  for (const e of expenses) {
    if (typeof e.payment_date !== "string") continue;
    if (!e.payment_date.startsWith(yearPrefix)) continue;
    count += 1;
    totalPaid += e.amount_cents ?? 0;
    mortgageInterest += e.deductible_mortgage_interest_cents ?? 0;
    propertyTax += e.deductible_property_tax_cents ?? 0;
    nonDeductible += e.non_deductible_portion_cents ?? 0;
  }
  return {
    mortgage_interest_total_cents: mortgageInterest,
    property_tax_total_cents: propertyTax,
    non_deductible_total_cents: nonDeductible,
    total_paid_cents: totalPaid,
    expense_count: count,
  };
}
